// T2-6186: expired/removed offer popups + Request Resubmit.
import React, {FC, useEffect, useState} from 'react'
import { useHistory } from 'react-router'
import dayjs from 'dayjs';
import _ from 'lodash';

import { TopBarRedesigned } from "components/TopBarRedesigned";
import { formatPartialOfferTime, debugTimeFormatting } from "utils/timeFormatting";
import {
    formatDentistPaymentTermsWithMethod,
    formatProfileHygienistName,
    getLatePaymentAgreementPrefix,
    getPaymentType,
} from "utils";
import { formatFirstLicensedLabel } from "utils/educationCredentials";
import { PartialOffer, JobPropsWithHygienist } from "utils/types";
import { Language } from "api/Api";

import FixedLoader from "../../../components/FixedLoader";
import useActionApi from "../../../hooks/useActionApi";
import Api from "../../../api";

import heartFilled from "../../../images/heartFilled.svg";
import heartUnfilled from "../../../images/heartUnfilled.svg";
import ProImage from "../../../components/Icons/ProImage";
import EliteImage from "../../../components/Icons/EliteImage";

import BookAnotherTemp from "../CustomOfferDetailsPage/components/BookAnotherTemp";
import RemoveShift from "../CustomOfferDetailsPage/components/RemoveShift";
import { JOB_STATUSES, PARTIAL_OFFER_STATUS } from "../../../utils/constants";
import { dismissOffer, filterDismissedOffers } from 'utils/dismissedOffers';
import { BrandedAlertModal } from "../../../components/Modal";
import type { BrandedAction } from "../../../components/Modal";
import { OfferClockIcon, OfferUnavailableIcon, OfferSentIcon } from "../../../components/Modal/offerModalIcons";
import { shouldPrefetchOfferResume } from 'utils/offerDetailsPayment';
import { getCanonicalMembershipBadge } from 'utils/membership';

import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import styles from './CustomOffersPage.module.scss';

const CustomOffersPage: FC = () => {
    const history = useHistory()
    const [isLoading, setIsLoading] = useState(false);

    const {id: jobId} = history.location.state as { id: number }

    const [job, setJob] = useState<JobPropsWithHygienist>()
    const [offers, setOffers] = useState<PartialOffer[]>(job?.partialOffers || [])
    const [isRemovedOfferModalOpened, setIsRemovedOfferModalOpened] = useState(false)
    const [isExpiredOfferModalOpened, setIsExpiredOfferModalOpened] = useState(false)
    const [isResubmitSentModalOpened, setIsResubmitSentModalOpened] = useState(false)
    const [resubmitErrorMessage, setResubmitErrorMessage] = useState<string | undefined>()
    const [selectedOffer, setSelectedOffer] = useState<PartialOffer>()

    /** Map hygienistId -> array of languages */
    const [languagesMap, setLanguagesMap] = useState<Record<number, Language[]>>({});

    const [getJobDetailsLoading, , getJobDetails] = useActionApi({
        apiMethod: Api.getJobDetails,
        onSuccess: (jobObj: any) => {
            if(_.isEmpty(jobObj)) return
            setJob(jobObj)
            // T2-5833: keep POSTED offers, plus EXPIRED (time-out) and REMOVED
            // (talent no longer available, e.g. booked elsewhere) so the DO can
            // see why an offer is gone instead of having it silently disappear.
            // Limit the EXPIRED / REMOVED bucket to offers from the current
            // posting cycle (offer.createdOn >= job.postedOn) so reposted shifts
            // don't surface stale cards from a prior cycle. ACCEPTED / REJECTED
            // are still hidden because those reflect resolved DO decisions.
            const jobPostedOn = jobObj?.postedOn ? new Date(jobObj.postedOn).getTime() : 0
            const isCurrentCycle = (offer: PartialOffer) => {
                if (!jobPostedOn) return true
                const created = offer?.createdOn ? new Date(offer.createdOn).getTime() : 0
                return created >= jobPostedOn
            }
            const filteredOffers = _.filter(jobObj.partialOffers, (o) => {
                if (o.isResubmitRequested) return false
                if (o.status === PARTIAL_OFFER_STATUS.POSTED) return true
                if (
                    o.status === PARTIAL_OFFER_STATUS.EXPIRED ||
                    o.status === PARTIAL_OFFER_STATUS.REMOVED
                ) {
                    return isCurrentCycle(o)
                }
                return false
            })
            setOffers(filterDismissedOffers(filteredOffers))

            // Prefetch languages for each hygienist
            filteredOffers.forEach((o: PartialOffer) => {
                // Active offers returned under the payment checkpoint contain
                // preview-only candidate data. Do not bypass that boundary by
                // prefetching the full resume from the list page.
                if (!shouldPrefetchOfferResume(o)) return;
                const hygIdFromOffer = (o as any).hygienistId || (o.hygienist as any)?.id;
                if (hygIdFromOffer && !languagesMap[hygIdFromOffer]) {
                    Api.getResumeData({
                        hygienistId: hygIdFromOffer,
                        locationId: jobObj.locationId,
                        offerId: o.id,
                    })
                        .then(res => {
                            if (res?.languages) {
                                setLanguagesMap(prev => ({ ...prev, [hygIdFromOffer]: res.languages }));
                            }
                        })
                        .catch(() => {});
                }
            });
        },
    });

    // T2-6186: hits the existing POST /v3/offers/:offerId/requestResubmit which
    // pushes/emails the talent and flips PartialOffer.isResubmitRequested = 1.
    const [resubmitLoading, , requestResubmit] = useActionApi({
        apiMethod: Api.requestResubmitOffer,
        onSuccess: () => {
            setIsExpiredOfferModalOpened(false)
            setOffers((prev) => prev.filter((o) => o.id !== selectedOffer?.id))
            setIsResubmitSentModalOpened(true)
        },
        onError: () => {
            // Keep the card visible and let the DO retry.
            setResubmitErrorMessage('Something went wrong sending the request. Please try again.')
        },
    });

    const onRequestResubmit = () => {
        if (!selectedOffer?.id) return
        setResubmitErrorMessage(undefined)
        requestResubmit({ offerId: selectedOffer.id })
    }

    // "Okay" on the EXPIRED popup dismisses and clears the card locally — except
    // for the defensive `isResubmitRequested` case, which only dismisses.
    const onExpiredOkay = () => {
        setIsExpiredOfferModalOpened(false)
        if (selectedOffer && !(selectedOffer as any).isResubmitRequested) {
            dismissOffer(selectedOffer.id)
            setOffers((prev) => prev.filter((o) => o.id !== selectedOffer.id))
        }
        setSelectedOffer(undefined)
    }

    const goToModifyJob = () => {
        history.push('modifyJob', { job })
    }

    const goHome = () => {
        history.push({ pathname: 'dentistHome' });
    }

    useEffect(() => {
        if (jobId) {
            getJobDetails(jobId)
        }
    }, [])

    const onOpenOffer = (id: number, partialOffer: any) => {
        setSelectedOffer(partialOffer)

        if (partialOffer.status === PARTIAL_OFFER_STATUS.REMOVED) {
            // T2-5833: explain "no longer available" and let DO dismiss with OK.
            setIsRemovedOfferModalOpened(true)
        } else if (partialOffer.status === PARTIAL_OFFER_STATUS.EXPIRED) {
            // T2-6186: time-expired offers now open an inline popup with a
            // one-click "Request Resubmit" action instead of routing to the
            // detail page. Skip the payment-info gate because the DO cannot
            // accept this offer anyway.
            setResubmitErrorMessage(undefined)
            setIsExpiredOfferModalOpened(true)
        } else {
            history.push('customOfferDetails', {
                offerId: id,
                job,
                partialOffer,
                offerDetailsSourcePage: 'custom_offers',
            })
        }
    }

    // T2-5858: the offer is already REMOVED on the backend — "Okay" just
    // dismisses the message on the DO's screen and clears the card from the
    // current list locally. No API call needed.
    const onRemoveOffer = (partialOfferId: number) => {
        setIsRemovedOfferModalOpened(false)
        dismissOffer(partialOfferId)
        setOffers((prev) => prev.filter((o) => o.id !== partialOfferId))
        setSelectedOffer(undefined)
    }

    // Get shift type label
    const getShiftTypeLabel = () => {
        if (job?.type === 2) return 'Admin Temping Shift';
        if (job?.type === 1) return 'Dental Assistant Shift';
        return 'Dental Hygienist Shift';
    };

    return (
        <div className={styles.pageContainer}>
            <TopBarRedesigned
                onBack={goHome}
                title={'Custom Offers'}
            />

            {selectedOffer && (
                <BrandedAlertModal
                    isOpen={isRemovedOfferModalOpened}
                    onClose={() => setIsRemovedOfferModalOpened(false)}
                    headerLabel="Custom Offer"
                    headerIcon={<OfferUnavailableIcon />}
                    illustration={<OfferUnavailableIcon />}
                    illustrationVariant="danger"
                    title={(selectedOffer as any)?.wasBookedElsewhere ? 'This Offer Is No Longer Available' : 'This Offer Was Withdrawn'}
                    ariaLabel="Offer no longer available"
                    actions={[{ label: 'Okay', variant: 'primary', onClick: () => onRemoveOffer(selectedOffer?.id) }]}
                >
                    {(selectedOffer as any)?.wasBookedElsewhere
                        ? `This ${job?.type === 1 ? 'assistant' : 'hygienist'} just got booked by another office for this day, sorry! Keep an eye out for more offers coming soon!`
                        : `This ${job?.type === 1 ? 'assistant' : 'hygienist'} removed their offer and it is no longer available for this day. Watch for more coming soon!`}
                </BrandedAlertModal>
            )}

            {selectedOffer && (() => {
                const fname = selectedOffer?.hygienist?.firstName || 'the talent';
                const alreadyRequested = !!(selectedOffer as any)?.isResubmitRequested;
                const expiredActions: BrandedAction[] = alreadyRequested
                    ? [{ label: 'Okay', variant: 'primary', onClick: onExpiredOkay }]
                    : [
                        { label: 'Okay', variant: 'secondary', disabled: resubmitLoading, onClick: onExpiredOkay },
                        { label: resubmitLoading ? 'Sending...' : 'Request Resubmit', variant: 'primary', disabled: resubmitLoading, onClick: onRequestResubmit },
                    ];
                return (
                    <BrandedAlertModal
                        isOpen={isExpiredOfferModalOpened}
                        onClose={onExpiredOkay}
                        headerLabel="Custom Offer"
                        headerIcon={<OfferClockIcon />}
                        illustration={<OfferClockIcon />}
                        illustrationVariant="danger"
                        title={alreadyRequested ? 'Resubmit Already Requested' : 'This Offer Has Expired'}
                        ariaLabel="Offer expired"
                        actions={expiredActions}
                    >
                        {alreadyRequested ? (
                            <>This offer expired and you've already asked {fname} to resubmit. Watch your email for a new offer if {fname} is still available.</>
                        ) : (
                            <>
                                {fname}'s offer for this shift timed out before it was accepted. You can ask {fname} to resubmit their offer — they'll get a notification and email and can resubmit if they're still available.
                                {resubmitErrorMessage && <div className={styles.resubmitError}>{resubmitErrorMessage}</div>}
                            </>
                        )}
                    </BrandedAlertModal>
                );
            })()}

            {selectedOffer && (
                <BrandedAlertModal
                    isOpen={isResubmitSentModalOpened}
                    onClose={() => { setIsResubmitSentModalOpened(false); setSelectedOffer(undefined); }}
                    headerLabel="Custom Offer"
                    headerIcon={<OfferSentIcon />}
                    illustration={<OfferSentIcon />}
                    illustrationVariant="success"
                    title="Resubmit Request Sent"
                    ariaLabel="Resubmit request sent"
                    actions={[{ label: 'Okay', variant: 'primary', onClick: () => { setIsResubmitSentModalOpened(false); setSelectedOffer(undefined); } }]}
                >
                    Resubmit request sent — watch your email for a new offer if {selectedOffer?.hygienist?.firstName || 'the talent'} is still available.
                </BrandedAlertModal>
            )}

            {job && (
                <main className={styles.mainContent}>
                    {/* Main Content Box */}
                    <div className={styles.mainContentBox}>
                        {/* Shift Details Card */}
                        <div className={styles.shiftDetailsCard}>
                            {/* Shift Type and Location */}
                            <div className={styles.shiftTypeRow}>
                                <h2 className={styles.shiftType}>{getShiftTypeLabel()}</h2>
                                <p className={styles.shiftLocation}>
                                    {`${job?.location.address}, ${job?.location.city}, ${job?.location.province} ${job?.location.postalCode}`}
                                </p>
                            </div>

                            {/* Date */}
                            <div className={styles.shiftDateRow}>
                                <DateRangeOutlinedIcon className={styles.shiftDateIcon} />
                                <span className={styles.shiftDate}>
                                    {dayjs(job?.shifts[0].shiftDate).format('dddd, MMM D')}
                                </span>
                            </div>

                            {/* Times */}
                            <div className={styles.shiftTimesRow}>
                                <div className={styles.shiftTimeItem}>
                                    <span className={styles.shiftTimeLabel}>Start:</span>
                                    <span className={styles.shiftTimeValue}>{job?.shifts[0].postedStart}</span>
                                </div>
                                <div className={styles.shiftTimeItem}>
                                    <span className={styles.shiftTimeLabel}>End:</span>
                                    <span className={styles.shiftTimeValue}>{job?.shifts[0].postedEnd}</span>
                                </div>
                            </div>

                            <button className={styles.changeHoursButton} onClick={goToModifyJob}>
                                Change hours
                            </button>

                            {/* Proposed Rate */}
                            <div className={styles.proposedRateRow}>
                                <span className={styles.proposedRateLabel}>Proposed Rate:</span>
                                <span className={styles.proposedRateValue}>${job?.proposedRate}/hr</span>
                            </div>
                        </div>

                        {/* Payment Terms */}
                        <div className={styles.paymentTermsRow}>
                            <span>
                                Payment terms:{' '}
                                {formatDentistPaymentTermsWithMethod(
                                    job?.paymentTerms,
                                    job?.paymentMethod,
                                    job?.location?.country,
                                )}
                            </span>
                            {!!job.location?.country && (
                                <span>Working Relationship: {getPaymentType(job.paysW2)}</span>
                            )}
                        </div>

                        <div className={styles.reminderLink}>
                            <span>{getLatePaymentAgreementPrefix(job?.type, job?.paymentTerms)}</span>
                            <a
                                href="https://www.tempstars.com/direct_payment"
                                target="_system"
                            >
                                TempStars Late Payment Policy
                            </a>
                        </div>

                        {/* Offers Section */}
                        {job?.Autobook !== 1 && offers.length > 0 && (
                            <div className={styles.offersSection}>
                                <h3 className={styles.offersSectionTitle}>
                                    {offers.length} {offers.length === 1 ? 'Offer' : 'Offers'} Received
                                </h3>

                                {offers.map((offer, index) => {
                                    const hygienist = offer.hygienist;
                                    const membershipBadge = getCanonicalMembershipBadge(
                                        hygienist?.membershipStatusCode ?? hygienist?.membershipSummary?.officeStatusLabel,
                                    );
                                    const professionalLicense = _.isEmpty(hygienist)
                                        ? '' : formatProfileHygienistName(hygienist, hygienist?.skills);
                                    
                                    const hourlyRate = `$${offer?.hourlyRate}/hr`;

                                    const expiredOffer = offer.status === PARTIAL_OFFER_STATUS.REMOVED
                                        || offer.status === PARTIAL_OFFER_STATUS.EXPIRED
                                        || job?.status === JOB_STATUSES.OFFER_EXPIRED;

                                    // T2-5833: distinguish time-expired from "no longer available"
                                    // (talent withdrew or got booked elsewhere) so the DO knows
                                    // why the offer can't be acted on and what it can do next.
                                    const offerStateLabel = (() => {
                                        if (offer.status === PARTIAL_OFFER_STATUS.REMOVED) {
                                            return 'No longer available';
                                        }
                                        if (offer.status === PARTIAL_OFFER_STATUS.EXPIRED) {
                                            return 'Offer expired';
                                        }
                                        return null;
                                    })();

                                    const isPartialHours = offer.offeredStartTime !== job?.shifts[0].postedStart || 
                                                         offer.offeredEndTime !== job?.shifts[0].postedEnd;

                                    if ((process.env.NODE_ENV as string) === 'staging' || (process.env.NODE_ENV as string) === 'local') {
                                      debugTimeFormatting(
                                        offer.offeredStartTime,
                                        offer.offeredStartTimeLocal,
                                        'CustomOffersPage - Offer Times'
                                      );
                                    }

                                    const formattedStartTime = formatPartialOfferTime(
                                      offer.offeredStartTime, 
                                      offer.offeredStartTimeLocal,
                                      offer.location?.lat,
                                      offer.location?.lon
                                    );
                                    const formattedEndTime = formatPartialOfferTime(
                                      offer.offeredEndTime, 
                                      offer.offeredEndTimeLocal,
                                      offer.location?.lat,
                                      offer.location?.lon
                                    );
                                    
                                    if (offer?.isResubmitRequested) {
                                        return null;
                                    }

                                    return (
                                        <div 
                                            key={offer.id || index} 
                                            className={`${styles.offerCard} ${expiredOffer ? styles.expired : ''}`}
                                            onClick={() => onOpenOffer(offer?.id, offer)}
                                        >
                                            <div className={styles.offerCardContent}>
                                                <div className={styles.offerLeftSection}>
                                                    {/* Profile Name Row */}
                                                    <div className={styles.offerProfileRow}>
                                                        <h4 className={styles.offerProfileName}>{professionalLicense}</h4>
                                                        <img 
                                                            src={offer?.isFavouriteHygienist ? heartFilled : heartUnfilled}
                                                            alt=""
                                                            className={styles.heartIcon}
                                                        />
                                                        {membershipBadge === 'pro' && (
                                                            <ProImage alt="Pro membership" style={{display: 'flex', alignSelf: 'center', margin: 'unset', width: 18, height: 18}} />
                                                        )}
                                                        {membershipBadge === 'elite' && (
                                                            <EliteImage alt="Elite membership" style={{display: 'flex', alignSelf: 'center', margin: 'unset', width: 18, height: 18}} />
                                                        )}
                                                        
                                                        {/* Language badges */}
                                                        {(languagesMap[offer.hygienistId] || [])
                                                            .filter((lang: Language) => lang.code !== 'en')
                                                            .map(lang => (
                                                                <span key={lang.id} className={styles.languageBadge}>
                                                                    {lang.code.toUpperCase()}
                                                                </span>
                                                            ))
                                                        }
                                                    </div>

                                                    {/* Graduated */}
                                                    <p className={styles.graduatedText}>
                                                        Graduated: {hygienist?.graduationYear}
                                                    </p>
                                                    {formatFirstLicensedLabel(
                                                        hygienist?.firstLicensedYear,
                                                        hygienist?.firstLicensedMonth,
                                                    ) ? (
                                                        <p className={styles.graduatedText}>
                                                            {formatFirstLicensedLabel(
                                                                hygienist?.firstLicensedYear,
                                                                hygienist?.firstLicensedMonth,
                                                            )}
                                                        </p>
                                                    ) : null}

                                                    {offerStateLabel && (
                                                        <p className={styles.offerStateLabel}>
                                                            {offerStateLabel}
                                                        </p>
                                                    )}

                                                    {/* Partial Hours */}
                                                    {isPartialHours && (
                                                        <div className={styles.partialHoursRow}>
                                                            <div className={styles.dashedCircle} />
                                                            <span className={styles.partialHoursText}>
                                                                Partial Hours Offered: {formattedStartTime} - {formattedEndTime}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Job Seeking Pill */}
                                                    {offer.jobSeekingFeatureActive &&
                                                     offer.showJobSeekingStatus === 1 &&
                                                     hygienist &&
                                                     hygienist.showForHire === 1 && (
                                                        <span className={styles.jobSeekingPill}>
                                                            Actively Seeking Permanent Employment
                                                        </span>
                                                    )}
                                                </div>

                                                <div className={styles.offerRightSection}>
                                                    <span className={styles.offerRate}>{hourlyRate}</span>
                                                    <button className={styles.viewDetailsButton}>
                                                        View Details
                                                        <ArrowForwardIcon />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* No offers state */}
                        {job?.Autobook !== 1 && offers.length === 0 && !getJobDetailsLoading && (
                            <div className={styles.noOffersState}>
                                <div className={styles.noOffersIcon}>📋</div>
                                <h3 className={styles.noOffersTitle}>No Offers Yet</h3>
                                <p className={styles.noOffersText}>
                                    Check back soon - hygienists are reviewing your shift posting.
                                </p>
                            </div>
                        )}

                        {/* Instabook Message */}
                        {job?.Autobook === 1 && !job?.cancelledOffer && (
                            <div className={styles.instabookMessage}>
                                You have submitted an Insta-Book Request to {job?.hygienist?.firstName} which will be confirmed or possibly declined within 15min.<br/><br/>
                                If you must cancel this shift, you'll have to wait until after the Insta-Book Request is accepted or declined.<br/><br/>
                                Watch your e-mail over the next 20min for notification about this Insta-Book Request status.
                            </div>
                        )}

                        {/* Bottom Buttons */}
                        <div className={styles.bottomButtons}>
                            <BookAnotherTemp job={job}/>
                            {job?.Autobook !== 1 || job?.cancelledOffer ? (
                                <RemoveShift job={job}/>
                            ) : null}
                        </div>
                    </div>
                </main>
            )}

            <FixedLoader loading={getJobDetailsLoading || isLoading}/>
        </div>
    )
}

export default CustomOffersPage
